/**
 * src/hooks/useAuth.js
 * 
 * Custom hook for authentication with email verification, optional phone verification,
 * password reset, and Google sign-in.
 * 
 * Provides:
 * - user: Current Firebase user object
 * - loading: Initial auth state loading
 * - emailVerified: Boolean indicating if email is verified
 * - phoneVerified: Boolean indicating if phone is verified
 * - login(email, password): Sign in with email/password
 * - signup(email, password): Create account
 * - loginWithGoogle(): Sign in with Google
 * - logout(): Sign out
 * - sendVerificationEmail(): Send verification email
 * - sendPasswordReset(email): Send password reset email
 * - startPhoneVerification(phoneNumber): Start phone auth flow
 * - confirmPhoneVerification(code): Confirm OTP
 * - linkPhoneToAccount(phoneNumber, code): Link phone to existing account
 * 
 * Usage:
 *   const { user, loading, login, logout } = useAuth();
 */

import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { reload } from "firebase/auth";
import {
  auth,
  onAuthStateChangedListener,
  signInWithEmail,
  createUserWithEmail,
  signOutUser,
  sendVerificationEmail,
  resetPassword,
  signInWithGoogle,
  signInWithPhoneAuth,
  setupRecaptchaVerifier,
  findEmailByUsername,
  findEmailByPhone,
} from "../lib/firebase";
import { getFriendlyAuthError } from "../utils/authErrorMessages";
import { warnDev, errorDev } from "../utils/logger";

function handleAuthError(error, t) {
  return getFriendlyAuthError(error, t, {
    defaultKey: "auth.errors.authError",
    defaultMessage: "Sorry, we could not complete that action right now. Please try again.",
  });
}

function useAuth() {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);
  const [phoneConfirmationResult, setPhoneConfirmationResult] = useState(null);

  /**
   * Initialize auth state listener on mount.
   */
  useEffect(() => {
    const unsubscribe = onAuthStateChangedListener((firebaseUser) => {
      if (firebaseUser) {
        // Normalize user object
        setUser({
          ...firebaseUser,
          emailVerified: firebaseUser.emailVerified || false,
          phoneNumber: firebaseUser.phoneNumber || null,
          hasPhoneVerified: !!firebaseUser.phoneNumber,
        });
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  /**
   * Set up reCAPTCHA verifier for phone verification.
   * Separate effect to manage lifecycle independently.
   */
  useEffect(() => {
    let verifier = null;

    try {
      const container = document.getElementById("recaptcha-container");
      if (container) {
        verifier = setupRecaptchaVerifier("recaptcha-container");
        setRecaptchaVerifier(verifier);
      }
    } catch (e) {
      warnDev("[useAuth] reCAPTCHA setup failed:", e);
    }

    return () => {
      // Clean up reCAPTCHA verifier
      if (verifier) {
        try {
          verifier.clear();
          setRecaptchaVerifier(null); // ✓ Clear state too
        } catch (e) {
          warnDev("[useAuth] reCAPTCHA clear failed:", e);
        }
      }
    };
  }, []);

  /**
   * Sign in with email and password.
   */
  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const result = await signInWithEmail(email, password);

      setUser({
        ...result.user,
        emailVerified: result.user.emailVerified,
        phoneNumber: result.user.phoneNumber || null,
        hasPhoneVerified: !!result.user.phoneNumber,
      });
      
      return result.user;
    } catch (err) {
      const errorMessage = handleAuthError(err, t);
      setError(errorMessage);
      throw err;
    }
  }, [t]);

  /**
   * Sign in with username + password.
   * Looks up the email associated with the username in Firestore,
   * then authenticates with Firebase Auth using that email.
   */
  const loginWithUsername = useCallback(async (username, password) => {
    setError(null);
    try {
      const email = await findEmailByUsername(username);
      if (!email) {
        const err = new Error("No account found with this username.");
        err.code = "auth/user-not-found";
        throw err;
      }
      return login(email, password);
    } catch (err) {
      if (!err.code) {
        const errorMessage = handleAuthError(err, t);
        setError(errorMessage);
      }
      throw err;
    }
  }, [login, t]);

  /**
   * Sign in with phone number + password.
   * Looks up the email associated with the phone in Firestore,
   * then authenticates with Firebase Auth using that email.
   */
  const loginWithPhone = useCallback(async (phone, password) => {
    setError(null);
    try {
      const email = await findEmailByPhone(phone);
      if (!email) {
        const err = new Error("No account found with this phone number.");
        err.code = "auth/user-not-found";
        throw err;
      }
      return login(email, password);
    } catch (err) {
      if (!err.code) {
        const errorMessage = handleAuthError(err, t);
        setError(errorMessage);
      }
      throw err;
    }
  }, [login, t]);

  /**
   * Sign up with email and password.
   * Automatically sends verification email.
   */
  const signup = useCallback(async (email, password) => {
    setError(null);
    try {
      const result = await createUserWithEmail(email, password);
      let verificationEmailSent = true;

      // Send verification email immediately
      try {
        await sendVerificationEmail(result.user);
      } catch (verifyErr) {
        verificationEmailSent = false;
        warnDev("[useAuth] Verification email failed:", verifyErr.message);
      }

      const userObj = {
        ...result.user,
        emailVerified: false,
        phoneNumber: null,
        hasPhoneVerified: false,
      };

      setUser(userObj);
      return {
        user: result.user,
        verificationEmailSent,
      };
    } catch (err) {
      const errorMessage = handleAuthError(err, t);
      setError(errorMessage);
      
      throw err;
    }
  }, [t]);

  /**
   * Sign in with Google.
   */
  const loginWithGoogle = useCallback(async () => {
    setError(null);
    try {
      const result = await signInWithGoogle();
      setUser({
        ...result.user,
        emailVerified: result.user.emailVerified,
        phoneNumber: result.user.phoneNumber || null,
        hasPhoneVerified: !!result.user.phoneNumber,
      });
      return result.user;
    } catch (err) {
      const errorMessage = handleAuthError(err, t);
      setError(errorMessage);
      throw err;
    }
  }, [t]);

  /**
   * Sign out current user.
   */
  const logout = useCallback(async () => {
    setError(null);
    try {
      await signOutUser();
      setUser(null);
      setPhoneConfirmationResult(null);
    } catch (err) {
      const errorMessage = handleAuthError(err, t);
      setError(errorMessage);
      throw err;
    }
  }, [t]);

  /**
   * Send verification email to current user.
   */
  const sendVerificationEmailAction = useCallback(async () => {
    setError(null);
    try {
      if (!user) throw new Error("No user signed in");
      await sendVerificationEmail(user);
      return true;
    } catch (err) {
      const errorMessage = handleAuthError(err, t);
      setError(errorMessage);
      throw err;
    }
  }, [user, t]);

  /**
   * Reload current user and return latest verification status.
   */
  const refreshVerificationStatus = useCallback(async () => {
    setError(null);
    try {
      if (!auth.currentUser) throw new Error("No user signed in");

      await reload(auth.currentUser);
      const refreshed = auth.currentUser;
      setUser(
        refreshed
          ? {
              ...refreshed,
              emailVerified: refreshed.emailVerified || false,
              phoneNumber: refreshed.phoneNumber || null,
              hasPhoneVerified: !!refreshed.phoneNumber,
            }
          : null
      );

      return Boolean(refreshed?.emailVerified);
    } catch (err) {
      const errorMessage = handleAuthError(err, t);
      setError(errorMessage);
      throw err;
    }
  }, [t]);

  /**
   * Send password reset email.
   */
  const sendPasswordResetEmail = useCallback(async (email) => {
    setError(null);
    try {
      await resetPassword(email);
      return true;
    } catch (err) {
      const errorMessage = handleAuthError(err, t);
      setError(errorMessage);
      throw err;
    }
  }, [t]);

  /**
   * Start phone verification flow (Firebase Phone Auth).
   * Lazy initializes reCAPTCHA verifier if needed.
   */
  const startPhoneVerification = useCallback(async (phoneNumber) => {
    setError(null);
    setVerifying(true);
    try {
      // Lazy initialize reCAPTCHA verifier if not ready
      let verifier = recaptchaVerifier;
      if (!verifier) {
        const container = document.getElementById("recaptcha-container");
        if (!container) {
          throw new Error("reCAPTCHA container not found - ensure <div id='recaptcha-container'></div> exists");
        }
        verifier = setupRecaptchaVerifier("recaptcha-container");
        if (!verifier) {
          throw new Error("Failed to initialize reCAPTCHA verifier");
        }
        setRecaptchaVerifier(verifier);
      }

      const confirmationResult = await signInWithPhoneAuth(
        phoneNumber,
        verifier
      );
      setPhoneConfirmationResult(confirmationResult);
      return confirmationResult;
    } catch (err) {
      const errorMessage = handleAuthError(err, t);
      setError(errorMessage);
      errorDev("[useAuth.startPhoneVerification] Error:", err.message);
      throw err;
    } finally {
      setVerifying(false);
    }
  }, [t, recaptchaVerifier]);

  /**
   * Confirm phone verification with OTP code.
   */
  const confirmPhoneVerification = useCallback(async (code) => {
    setError(null);
    setVerifying(true);
    try {
      if (!phoneConfirmationResult) {
        throw new Error("No phone verification in progress");
      }

      const result = await phoneConfirmationResult.confirm(code);
      setUser({
        ...result.user,
        emailVerified: result.user.emailVerified,
        phoneNumber: result.user.phoneNumber || null,
        hasPhoneVerified: !!result.user.phoneNumber,
      });
      setPhoneConfirmationResult(null);
      return result.user;
    } catch (err) {
      const errorMessage = handleAuthError(err, t);
      setError(errorMessage);
      throw err;
    } finally {
      setVerifying(false);
    }
  }, [phoneConfirmationResult, t]);

  /**
   * Link phone to existing account (for logged-in users).
   */
  const linkPhoneToAccount = useCallback(async (phoneNumber, code) => {
    setError(null);
    setVerifying(true);
    try {
      if (!user) throw new Error("No user signed in");
      
      const result = await startPhoneVerification(phoneNumber);
      const confirmed = await result.confirm(code);
      
      setUser({
        ...confirmed.user,
        emailVerified: confirmed.user.emailVerified,
        phoneNumber: confirmed.user.phoneNumber || null,
        hasPhoneVerified: !!confirmed.user.phoneNumber,
      });
      return confirmed.user;
    } catch (err) {
      const errorMessage = handleAuthError(err, t);
      setError(errorMessage);
      throw err;
    } finally {
      setVerifying(false);
    }
  }, [user, startPhoneVerification, t]);

  return {
    user,
    loading,
    error,
    verifying,
    emailVerified: user?.emailVerified || false,
    phoneVerified: user?.hasPhoneVerified || false,
    login,
    loginWithUsername,
    loginWithPhone,
    signup,
    loginWithGoogle,
    logout,
    sendVerificationEmail: sendVerificationEmailAction,
    refreshVerificationStatus,
    sendPasswordReset: sendPasswordResetEmail,
    startPhoneVerification,
    confirmPhoneVerification,
    linkPhoneToAccount,
  };
}

// Support both named and default imports
export { useAuth };
export default useAuth;
